// src/contexts/DashboardContext.tsx
'use client';

import React, { createContext, useContext, ReactNode, useState, useEffect } from 'react';
import { GenderStats, PerformanceData, TopAudience, TopCreative } from 'src/types/performance';
import { useHttp } from 'src/hooks';
import { AuthContext } from './auth-context';

interface DashboardContextType {
  performanceData: PerformanceData | null;
  genderStats: GenderStats | null;
  topAudience: TopAudience | null;
  topCreatives: TopCreative[];
  isLoading: boolean;
  fetchDashboardData: () => Promise<void>;
  refreshData: () => void;
}

const DashboardContext = createContext<DashboardContextType | undefined>(undefined);

export const DashboardProvider = ({ children }: { children: ReactNode }) => {
  const { sendRequest, isLoading } = useHttp();
  const { accessToken, profile } = useContext(AuthContext);
  const [performanceData, setPerformanceData] = useState<PerformanceData | null>(null);
  const [genderStats, setGenderStats] = useState<GenderStats | null>(null);
  const [topAudience, setTopAudience] = useState<TopAudience | null>(null);
  const [topCreatives, setTopCreatives] = useState<TopCreative[]>([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const refreshData = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  const fetchDashboardData = async () => {
    if (!profile?.facebook?.accessToken) return;
    try {
      await sendRequest(
        {
          url: `campaign/overview`,
          method: 'GET',
          accessToken,
        },
        (data: any) => {
          if (data) {
            setPerformanceData(data.performanceData);
            setGenderStats(data.genderStats);
            setTopAudience(data.topAudience);
            setTopCreatives(data.topCreatives);
          }
        },
      );
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    }
  };

  // useEffect(() => {
  //   if (accessToken) {
  //     fetchDashboardData();
  //   }
  // }, [accessToken, refreshTrigger, profile]);

  return (
    <DashboardContext.Provider
      value={{
        performanceData,
        genderStats,
        topAudience,
        topCreatives,
        isLoading,
        fetchDashboardData,
        refreshData,
      }}
    >
      {children}
    </DashboardContext.Provider>
  );
};

export const useDashboard = () => {
  const context = useContext(DashboardContext);
  if (context === undefined) {
    throw new Error('useDashboard must be used within a DashboardProvider');
  }
  return context;
};
